/**
 * Wire types for the product-usage stream, mirroring the Pydantic DTOs verbatim.
 *
 * `AnalyticsEventName` is a closed union on purpose. An open string means a typo
 * silently removes a metric from the dashboard and nobody ever finds out; a union
 * makes the same typo a type error before the code runs.
 */

export const AnalyticsEventName = {
  SessionStart: "session_start",
  AppForeground: "app_foreground",
  AppBackground: "app_background",
  ScreenView: "screen_view",

  ContentView: "content_view",
  ContentDwell: "content_dwell",
  ContentScroll: "content_scroll",

  VideoStart: "video_start",
  VideoProgress: "video_progress",

  EventView: "event_view",
  EventCtaClick: "event_cta_click",
  EventAddToCalendar: "event_add_to_calendar",
  EventRegister: "event_register",
  EventJoinStream: "event_join_stream",
  EventQuestionAsked: "event_question_asked",
  ContentSave: "content_save",

  HighlightImpression: "highlight_impression",
  HighlightClick: "highlight_click",
  FeedImpression: "feed_impression",
  FeedClick: "feed_click",

  FilterTopicApplied: "filter_topic_applied",
  FilterFormatApplied: "filter_format_applied",
  LibraryLoadMore: "library_load_more",
  CalendarMonthChange: "calendar_month_change",
  CalendarDaySelect: "calendar_day_select",
  /** Reserved. Search does not exist in the product yet, so nothing emits this. */
  SearchPerformed: "search_performed",

  NotificationsOpen: "notifications_open",
  NotificationClick: "notification_click",
  /** The OS dialog came back granted — the only observable outcome of asking. */
  PushPermissionGranted: "push_permission_granted",

  ChatOpen: "chat_open",
  PaywallView: "paywall_view",
  CheckoutStarted: "checkout_started",
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEventName)[keyof typeof AnalyticsEventName];

export type AnalyticsSubjectType =
  | "news"
  | "event"
  | "highlight"
  | "topic"
  | "format"
  | "chat"
  | "notification"
  | "shop";

export type AnalyticsPlatform = "ios" | "android" | "web";

export type AnalyticsProps = Record<string, string | number | boolean>;

export interface AnalyticsEvent {
  /** Client-minted. The server dedupes on it, so a replayed batch costs nothing. */
  id: string;
  name: AnalyticsEventName;
  /** When it happened, not when it was sent — a queued event may be hours old. */
  occurred_at: string;
  session_id: string;
  /** Route pattern, never the resolved path: "/news/:slug", not "/news/rytmus". */
  screen: string | null;
  subject_type: AnalyticsSubjectType | null;
  subject_id: string | null;
  /** The slug. It is what identifies content across both locales. */
  subject_key: string | null;
  content_format: string | null;
  /** Milliseconds for dwell, percent for progress. */
  value: number | null;
  props: AnalyticsProps;
}

export interface AnalyticsBatch {
  /**
   * The client's clock at send time. The server compares it with its own arrival
   * time and shifts every `occurred_at` by the difference, so a device with a wrong
   * clock still lands on the right day and keeps its intervals intact.
   */
  sent_at: string;
  locale: string;
  platform: AnalyticsPlatform;
  app_version: string | null;
  events: AnalyticsEvent[];
}

export interface AnalyticsIngestResult {
  accepted: number;
  rejected: number;
}
